import React, { useEffect } from "react";
import { useGetFetchNew } from "../../data/Queries";
import {
  PeopleIcon,
  WarningIcon,
  PersonIcon,
  CompletedIcon,
  ReceptionistIcon,
} from "../../images/icons/icons";
import moment from "moment";

const TrackingStats = ({
  userInfoData,
  refetchTrigger,
  onNavigateToOutOfRange,
}) => {
  const pageLimit = 10;
  const isPage = 0;

  const fromDate = moment().startOf("day").format("YYYY-MM-DD");
  const toDate = moment().endOf("day").format("YYYY-MM-DD");
  const fromTime = moment().startOf("hour").format("HH:mm");
  const toTime = moment().startOf("hour").add(1, "hour").format("HH:mm");

  const {
    data: TrackingStatsData,
    isLoading: trackingLoading,
    refetch: refetchTracking,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "shift_stats",
    url: `/user/shift-stats?limit=${pageLimit}&page=${isPage + 1}&from_date=${fromDate}&to_date=${toDate}&from_time=${fromTime}&to_time=${toTime}`,
  });

  const statsData = TrackingStatsData?.data;

  // Refetch when refetchTrigger changes
  useEffect(() => {
    if (refetchTrigger) {
      refetchTracking();
    }
  }, [refetchTrigger, refetchTracking]);

  const handleCardClick = (cardType, metric) => {
    console.log(`Clicked ${cardType} - ${metric}`);

    // Navigate to Out of Range Logs tab when clicking out of range metrics
    if (metric === "out_of_range" && onNavigateToOutOfRange) {
      // Pass the staff type filter based on which card was clicked
      onNavigateToOutOfRange(
        cardType === "trainer" ? "trainer" : "receptionist"
      );
    }
  };

  const handleRefresh = () => {
    refetchTracking();
  };

  if (trackingLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px",
        }}
      >
        <div
          style={{
            animation: "spin 1s linear infinite",
            borderRadius: "50%",
            height: "32px",
            width: "32px",
            border: "2px solid #e5e7eb",
            borderTop: "2px solid #3b82f6",
          }}
        ></div>
        <span style={{ marginLeft: "8px", color: "#6b7280" }}>
          Loading stats...
        </span>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <div
        style={{
          marginBottom: "32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              color: "#1f2937",
              marginBottom: "8px",
            }}
          >
            Staff Performance Dashboard
          </h2>
          <p style={{ color: "#6b7280", fontSize: "16px" }}>
            Comprehensive overview of trainer and receptionist metrics
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={trackingLoading}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 20px",
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: trackingLoading ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            opacity: trackingLoading ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!trackingLoading) {
              e.currentTarget.style.backgroundColor = "#2563eb";
              e.currentTarget.style.transform = "translateY(-1px)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#3b82f6";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              animation: trackingLoading ? "spin 1s linear infinite" : "none",
            }}
          >
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
          </svg>
          {trackingLoading ? "Refreshing..." : "Refresh Stats"}
        </button>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Main Stats Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "32px",
          marginBottom: "32px",
        }}
      >
        {/* Overall Summary Card - Staff Count */}
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "16px",
            padding: "24px",
            color: "white",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "20px",
              paddingBottom: "16px",
              borderBottom: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            <div style={{ marginRight: "12px" }}>
              <PeopleIcon color="#fff" size={24} />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: "600" }}>
              Staff Overview
            </h3>
          </div>

          {/* Total Staff Count */}
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{ fontSize: "14px", opacity: "0.9", marginBottom: "4px" }}
            >
              Total Staff
            </div>
            <div style={{ fontSize: "36px", fontWeight: "bold" }}>
              {parseInt(statsData?.total_trainers || "0") +
                parseInt(statsData?.total_receptionists || "0")}
            </div>
          </div>

          {/* Breakdown */}
          <div style={{ display: "grid", gap: "12px", marginTop: "16px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "12px",
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "8px",
                color: "white",
              }}
            >
              <span style={{ fontSize: "14px", color: "white" }}>
                👨‍🏫 Trainers
              </span>
              <span
                style={{ fontSize: "18px", fontWeight: "bold", color: "white" }}
              >
                {statsData?.total_trainers || "0"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "12px",
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "8px",
                color: "white",
              }}
            >
              <span style={{ fontSize: "14px", color: "white" }}>
                👔 Receptionists
              </span>
              <span
                style={{ fontSize: "18px", fontWeight: "bold", color: "white" }}
              >
                {statsData?.total_receptionists || "0"}
              </span>
            </div>
          </div>
        </div>

        {/* Trainer Section */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            padding: "24px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "20px",
              paddingBottom: "16px",
              borderBottom: "2px solid #3b82f6",
            }}
          >
            <div style={{ marginRight: "12px" }}>
              <PersonIcon color="#3b82f6" />
            </div>
            <h3
              style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}
            >
              Trainers
            </h3>
          </div>

          <div style={{ display: "grid", gap: "16px" }}>
            {/* Trainer Check-ins */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px",
                backgroundColor: "#f0fdf4",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onClick={() => handleCardClick("trainer", "checkins")}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#dcfce7";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f0fdf4";
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <CompletedIcon />
                <span
                  style={{
                    marginLeft: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Checked In
                </span>
              </div>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#16a34a",
                }}
              >
                {statsData?.check_in_trainers || "0"}
              </span>
            </div>

            {/* Trainer Out of Range */}
            <div
              style={{
                padding: "16px",
                backgroundColor: "#fef2f2",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "2px solid transparent",
              }}
              onClick={() => handleCardClick("trainer", "out_of_range")}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fecaca";
                e.currentTarget.style.borderColor = "#dc2626";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(220, 38, 38, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fef2f2";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <WarningIcon />
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Out of Range
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#dc2626",
                  }}
                >
                  {statsData?.total_trainer_out_of_range || "0"}
                </span>
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#991b1b",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "4px",
                  fontWeight: "500",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>
                  Last 1 hour ({moment().startOf("hour").format("hh:mm A")} -{" "}
                  {moment().format("hh:mm A")})
                </span>
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#7f1d1d",
                  marginTop: "6px",
                  fontStyle: "italic",
                  backgroundColor: "#fee2e2",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <span>👆</span>
                <span>Click for TRAINER out-of-range logs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Receptionist Section */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            padding: "24px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "20px",
              paddingBottom: "16px",
              borderBottom: "2px solid #f59e0b",
            }}
          >
            <div style={{ marginRight: "12px" }}>
              <ReceptionistIcon />
            </div>
            <h3
              style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}
            >
              Receptionists
            </h3>
          </div>

          <div style={{ display: "grid", gap: "16px" }}>
            {/* Receptionist Check-ins */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px",
                backgroundColor: "#fffbeb",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onClick={() => handleCardClick("receptionist", "checkins")}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fef3c7";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fffbeb";
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <CompletedIcon />
                <span
                  style={{
                    marginLeft: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Checked In
                </span>
              </div>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#d97706",
                }}
              >
                {statsData?.check_in_receptionists || "0"}
              </span>
            </div>

            {/* Receptionist Out of Range */}
            <div
              style={{
                padding: "16px",
                backgroundColor: "#fff7ed",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "2px solid transparent",
              }}
              onClick={() => handleCardClick("receptionist", "out_of_range")}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fed7aa";
                e.currentTarget.style.borderColor = "#c2410c";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(194, 65, 12, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fff7ed";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <WarningIcon />
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Out of Range
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#c2410c",
                  }}
                >
                  {statsData?.total_receptionist_out_of_range || "0"}
                </span>
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#92400e",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "4px",
                  fontWeight: "500",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>
                  Last 1 hour ({moment().startOf("hour").format("hh:mm A")} -{" "}
                  {moment().format("hh:mm A")})
                </span>
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#78350f",
                  marginTop: "6px",
                  fontStyle: "italic",
                  backgroundColor: "#ffedd5",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <span>👆</span>
                <span>Click for RECEPTIONIST out-of-range logs</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "16px",
          padding: "24px",
          color: "white",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ fontSize: "18px", fontWeight: "600" }}>
            Performance Summary
          </h3>
          <div style={{ fontSize: "12px", opacity: "0.8" }}>
            Last Updated: {statsData?.last_updated || "-"}
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "20px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              {statsData?.total_out_of_range || "0"}
            </div>
            <div style={{ fontSize: "13px", opacity: "0.9" }}>
              Total Out of Range
            </div>
            <div style={{ fontSize: "11px", opacity: "0.7", marginTop: "4px" }}>
              (Last 1 hour)
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              {statsData?.total_trainers_in_location || "0"}
            </div>
            <div style={{ fontSize: "13px", opacity: "0.9" }}>
              Trainers Checked In
            </div>
            <div style={{ fontSize: "11px", opacity: "0.7", marginTop: "4px" }}>
              Currently active
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              {statsData?.total_receptionists_in_location || "0"}
            </div>
            <div style={{ fontSize: "13px", opacity: "0.9" }}>
              Receptionists Checked In
            </div>
            <div style={{ fontSize: "11px", opacity: "0.7", marginTop: "4px" }}>
              Currently active
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingStats;
